import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ControlConfig } from '../../types/form.types';

/**
 * Form checkbox input component with validation and interaction states.
 *
 * @remarks Provides a checkbox input with validation support, hover states,
 * keyboard interaction, error messaging, and accessibility features. Integrates
 * with Angular reactive forms and supports dynamic configuration through
 * the ControlConfig interface.
 */
@Component({
  selector: 'app-form-checkbox',
  templateUrl: './form-checkbox.component.html',
  styleUrls: ['./form-checkbox.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormCheckboxComponent implements OnInit, OnChanges {
  /** @remarks Configuration object defining the checkbox behavior and validation */
  @Input() controlConfig: ControlConfig;

  /** @remarks Form group that contains this checkbox control */
  @Input() formGroup: FormGroup;

  /** @remarks External error state flag for custom validation scenarios */
  @Input() hasError: boolean = false;

  /** @remarks Current hover state for styling purposes */
  isHovered: boolean = false;

  /**
   * Determines if the checkbox is in an invalid state.
   *
   * @remarks Checks if the form control is invalid and has been interacted with
   * (touched or dirty) or if an external error flag is set. Used for styling
   * and error message display.
   *
   * @returns True if the checkbox should show validation errors
   */
  get isInvalid(): boolean {
    const control = this.formGroup?.get(this.controlConfig.name);
    return (control?.invalid && (control?.touched || control?.dirty)) || this.hasError;
  }

  /**
   * Determines if the checkbox is in a valid state.
   *
   * @remarks Checks if the form control is valid and has been interacted with.
   * Used for success styling indicators.
   *
   * @returns True if the checkbox should show validation success
   */
  get isValid(): boolean {
    const control = this.formGroup?.get(this.controlConfig.name);
    return control?.valid && (control?.touched || control?.dirty);
  }

  /**
   * Gets the current checked state of the checkbox.
   *
   * @remarks Returns true if the checkbox is checked (value is true),
   * false otherwise. Used for determining the visual state and
   * accessibility attributes.
   *
   * @returns True if the checkbox is checked
   */
  get isChecked(): boolean {
    const control = this.formGroup?.get(this.controlConfig.name);
    return control?.value === true;
  }

  /**
   * Gets the unique ID for the checkbox input element.
   *
   * @remarks Generates a unique identifier for the checkbox element
   * to ensure proper form control and label association.
   *
   * @returns A unique ID string for the checkbox element
   */
  get checkboxId(): string {
    return `checkbox-${this.controlConfig.name}`;
  }

  /**
   * Gets the error message element ID for accessibility.
   *
   * @remarks Generates a unique ID for the error message element to be
   * used in aria-describedby attributes for proper screen reader association.
   *
   * @returns The ID string for the error message element
   */
  get errorId(): string {
    return `error-message-${this.controlConfig.name}`;
  }

  /**
   * Gets the appropriate error message to display.
   *
   * @remarks Determines the correct error message based on validation failures,
   * particularly checking for required field violations when the checkbox
   * is not checked.
   *
   * @returns The error message string or null if no errors
   */
  get errorMessage(): string | null {
    if (this.isInvalid) {
      // For checkbox, errorMessageRequired makes most sense as it's usually required
      if (this.controlConfig.required && !this.isChecked) {
        return this.controlConfig.errorMessageRequired || '';
      }
      return this.controlConfig.errorMessage || '';
    }
    return null;
  }

  /**
   * Component initialization lifecycle hook.
   *
   * @remarks Updates the form control's disabled state based on the
   * initial configuration when the component is first initialized.
   */
  ngOnInit(): void {
    this.updateDisabledState();
  }

  /**
   * Handles changes to component inputs.
   *
   * @remarks Updates the form control's disabled state when the control
   * configuration changes, ensuring the UI stays in sync with the config.
   *
   * @param changes - Object containing the changed input properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['controlConfig'] && !changes['controlConfig'].firstChange) {
      this.updateDisabledState();
    }
  }

  /**
   * Handles checkbox change events.
   *
   * @remarks Updates the form control value when the checkbox state changes,
   * marks the control as touched and dirty for validation purposes.
   * Only processes changes if the control is not disabled.
   *
   * @param event - The change event from the checkbox input
   */
  onCheckboxChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const control = this.formGroup.get(this.controlConfig.name);

    if (control && !control.disabled) {
      control.setValue(checkbox.checked);
      control.markAsTouched();
      control.markAsDirty();
    }
  }

  /**
   * Handles mouse enter events for hover state.
   *
   * @remarks Sets the hover state to true for styling purposes when
   * the user's mouse enters the checkbox area.
   */
  onMouseEnter(): void {
    this.isHovered = true;
  }

  /**
   * Handles mouse leave events for hover state.
   *
   * @remarks Sets the hover state to false for styling purposes when
   * the user's mouse leaves the checkbox area.
   */
  onMouseLeave(): void {
    this.isHovered = false;
  }

  /**
   * Handles keyboard events for accessibility.
   *
   * @remarks Provides keyboard interaction support by allowing Enter and
   * Spacebar keys to toggle the checkbox state, improving accessibility
   * for keyboard-only users.
   *
   * @param event - The keyboard event
   */
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const checkbox = event.target as HTMLInputElement;
      checkbox.click();
    }
  }

  /**
   * Updates the form control's enabled/disabled state.
   *
   * @remarks Enables or disables the form control based on the configuration
   * settings, ensuring proper form behavior and accessibility.
   */
  private updateDisabledState(): void {
    const control = this.formGroup?.get(this.controlConfig.name);
    if (control) {
      if (this.controlConfig.disabled) {
        control.disable();
      } else {
        control.enable();
      }
    }
  }
}
